import { DateTime } from 'luxon';
import { parseDate } from '@/util/formatDate';
import { ISnowHistoryItem } from '@/api/resort/types';

// Recent Snowfall is hinged to when a resort last reported, so quiet /
// off-season resorts can serve weeks-old data. This buckets the report's
// age so the UI can render it honestly. See
// docs/superpowers/specs/2026-06-25-recent-snowfall-staleness-design.md
export const FRESH_MAX_HOURS = 36;
export const STALE_MAX_DAYS = 5;

export type RecentSnowfallState = 'fresh' | 'stale' | 'expired';

export interface RecentSnowfallFreshness {
  state: RecentSnowfallState;
  /** The anchor date the bucket was computed from (for display), or null. */
  reportedAt: DateTime | null;
}

const toValidDate = (raw?: string): DateTime | null => {
  if (!raw) return null;
  const dt = parseDate(raw);
  return dt.isValid ? dt : null;
};

const latestRecentDate = (recent?: ISnowHistoryItem[]): DateTime | null => {
  const dates = (recent ?? [])
    .map((d) => toValidDate(d.date))
    .filter((d): d is DateTime => d !== null);
  if (dates.length === 0) return null;
  return dates.reduce((a, b) => (b.toMillis() > a.toMillis() ? b : a));
};

export const getRecentSnowfallFreshness = (
  updatedAt: string | undefined,
  recent: ISnowHistoryItem[] | undefined,
  now: DateTime = DateTime.now(),
): RecentSnowfallFreshness => {
  const anchor = toValidDate(updatedAt) ?? latestRecentDate(recent);
  if (!anchor) return { state: 'expired', reportedAt: null };

  // A future-dated anchor (clock skew / bad timestamp) yields a negative
  // age and is treated as fresh — we don't hide a report for being "early".
  const ageHours = now.diff(anchor, 'hours').hours;
  if (ageHours <= FRESH_MAX_HOURS) return { state: 'fresh', reportedAt: anchor };
  if (ageHours <= STALE_MAX_DAYS * 24) return { state: 'stale', reportedAt: anchor };
  return { state: 'expired', reportedAt: anchor };
};
